import { createFakeStudioApi } from './studioApi.fake';
import { sampleAnswer, sampleProfiles, sampleScript, sampleSuggestions, sampleTopics, sampleTranscript } from './studioSamples';
import { LIBRARY, artSuggestions, generateArt, pickTemplate, placeAttachments, reviseArt, templateDocument, templateEntry } from './artSamples';
import { asksForImages } from './artImages';
import { communityCandidates, rankByWords } from './communityImages';
import { applyTemplate, blobToDataUrl, ensureDocumentFonts, invalidateTextLayouts, prepareImageFile } from '../features/ArtEditor/headless';

// `/studio?mock=true[&scenario=<name>]` runs the Studio on the in-memory double
// instead of core-platform, until `?mock=false`. The store lives in this
// tab's sessionStorage per scenario, so a reload resumes where it stopped;
// the worker's part (profile analysis, transcriptions, run phases) advances
// with elapsed time. Transcriptions return a sample text, not the recording's.
// Payments are on in every scenario: `pay` stands in for the Stripe embedded
// checkout and the webhook fulfils the paid session a moment later. So is Personality
// (contract 2.3.0): a mapping downloads, transcribes and analyzes by elapsed time, and
// `personality_fails` ends the first one with too little speech in its second video, so
// swapping only that one maps the personality. Every personality also compares the best and
// worst reels by reach (contract 2.5.0, `ready`); three scenarios reach its other outcomes.
// Images in the arts (contract 2.9.0) are on in every scenario: a new art whose template has
// photos, or an art the creator asks images for, stops on the image question; the answer is
// placed after a pause per image generated, and "generating" picks the community or gallery
// photo that best matches the subject (else draws a gradient card with it), saved as `generated`.
const MOCK_KEY = 'ginastee:studio:mock';
// v7: actions carry `images`/`preview`, quotes `action_id` and gallery images their description
// (2.9.0); a saved v6 store has none of them.
const STATE_KEY = 'ginastee:studio:mock:state:v7';

export const MOCK_SCENARIOS = {
  found: { onboarding: true },
  generic: { onboarding: true, analysis: 'none' },
  not_found: { onboarding: true, lookup: 'not_found' },
  meta_down: { onboarding: true, lookup: 'unavailable' },
  rate_limited: { onboarding: true, lookup: 'rate_limited' },
  no_lookup: { onboarding: true, platforms: [] },
  onboarding_off: { onboarding: false },
  creator: { onboarding: true, creator: true },
  // Two more Instagram profiles connected after the creator's own (contract 1.9.0).
  profiles: { onboarding: true, creator: true, profiles: true },
  standard: { onboarding: true, creator: true, plan: 'standard' },
  pro: { onboarding: true, creator: true, plan: 'pro' },
  // A Standard subscriber whose credits ran out: the next generation opens the upgrade.
  empty: { onboarding: true, creator: true, plan: 'standard', credits: 0 },
  // The renewal could not be charged two days ago: an open invoice, card to replace.
  past_due: { onboarding: true, creator: true, plan: 'standard', status: 'past_due' },
  // A Pro subscriber who cancelled: access until the end of the cycle, then free.
  canceling: { onboarding: true, creator: true, plan: 'pro', cancel: true },
  // Personality: a profile whose personality was mapped six days ago.
  personality: { onboarding: true, creator: true, personality: true },
  // Personality: the first mapping fails on its second video (too little speech); the
  // others are kept, and a mapping without that video completes.
  personality_fails: { onboarding: true, creator: true, personalityFailure: 'insufficient_speech' },
  // What works for the creator, other than `ready`: reach too even to compare, too few reels
  // to compare, and best reels with nothing in common that the others lack.
  personality_no_standouts: { onboarding: true, creator: true, personality: true, performance: 'no_standouts' },
  personality_few_videos: { onboarding: true, creator: true, personality: true, performance: 'insufficient_videos' },
  personality_no_pattern: { onboarding: true, creator: true, personality: true, performance: 'no_pattern' },
  // Direct Messages (contract 2.14.0 proposal, routes in studioAutomations.fake.js): a connected
  // account with automations, results and an inbox. Every other scenario starts unconnected,
  // so its connection plays the whole flow, and `automations_new` is that on a paid plan.
  automations: { onboarding: true, creator: true, plan: 'standard', directMessages: 'connected' },
  automations_pro: { onboarding: true, creator: true, plan: 'pro', directMessages: 'connected' },
  automations_free: { onboarding: true, creator: true, directMessages: 'connected' },
  automations_new: { onboarding: true, creator: true, plan: 'standard' },
  automations_empty: { onboarding: true, creator: true, plan: 'standard', directMessages: 'empty' },
  automations_reauth: { onboarding: true, creator: true, plan: 'standard', directMessages: 'reauth' },
  automations_permissions: { onboarding: true, creator: true, plan: 'standard', directMessages: 'missing' },
};
const DEFAULT_SCENARIO = 'found';
const LATENCY_MS = { startOnboarding: 1300, completeOnboarding: 900, createProject: 600 };
const ANALYSIS_MS = 3600;
const TRANSCRIPTION_MS = 2400;
// Elapsed ms → run status and phase; the script is written at the last step.
const RUN_STEPS = [[1200, 'running', 'brief'], [2600, 'running', 'evidence'], [4200, 'running', 'writing'], [6600, 'running', 'review'], [8000, 'running', 'persisting']];
const RUN_DONE_MS = 9000;
// A question (the source message ends with "?") is answered in the chat instead (1.7.0).
const ANSWER_MS = 2400;
// A paid checkout waits this long for its webhook.
const WEBHOOK_MS = 2600;
// Elapsed ms → personality mapping stage (and sources transcribed so far); done at MAPPING_DONE_MS.
// As in core, the 3 chosen Reels are transcribed first and the stage stays on transcribing
// while the reels of the comparison follow. A mapping without a sample to compare goes from
// transcribing straight to analyzing.
const MAPPING_STEPS = [[900, 'downloading', 0], [3000, 'transcribing', 0], [3800, 'transcribing', 1], [4600, 'transcribing', 2], [5400, 'transcribing', 3],
  [7600, 'comparing', 3], [10600, 'analyzing', 3]];
const MAPPING_DONE_MS = 13400;
// Arts (contract 2.7.0 proposal): an art run designs after writing; done at ART_DONE_MS.
const ART_STEPS = [[900, 'running', 'brief'], [2000, 'running', 'writing'], [3600, 'running', 'designing'], [5600, 'running', 'review'], [6800, 'running', 'persisting']];
const ART_DONE_MS = 7600;
// The art is designed when `designing` ends; the image question (2.9.0) comes right then.
const ART_DESIGNED_MS = 5600;
// After the image question is answered (2.9.0): the images phase, longer for each image
// generated, then review and persisting before the art is saved.
const IMAGES_MS = 1400;
const IMAGE_GENERATE_MS = 900;
const IMAGES_TAIL = [[0, 'review'], [600, 'persisting']];
const IMAGES_DONE_MS = 1200;
// Uploaded images are kept apart from the store (data URLs are heavy), downscaled. v2 with
// the v7 store: the asset ids start over.
const ASSETS_KEY = 'ginastee:studio:mock:assets:v2';
// The creator's sample gallery as core's vision model would have left it (2.9.0): described,
// and two not described yet (null until then).
const GALLERY = [
  ['bowl', { pt_BR: 'Tigela de salada vista de cima com tofu, ovos de codorna, edamame, milho e folhas verdes.', en_US: 'Overhead salad bowl with tofu, quail eggs, edamame, corn and greens.',
    es_ES: 'Bol de ensalada visto desde arriba con tofu, huevos de codorniz, edamame, maíz y hojas verdes.' }],
  ['breakfast', null],
  ['floor', { pt_BR: 'Pessoa fazendo abdominais no chão de um estúdio de treino.', en_US: 'Person doing crunches on the floor of a training studio.', es_ES: 'Persona haciendo abdominales en el suelo de un estudio de entrenamiento.' }],
  ['yoga', { pt_BR: 'Silhueta de mulher em postura de yoga à beira-mar ao entardecer.', en_US: 'Silhouette of a woman in a yoga pose by the sea at dusk.', es_ES: 'Silueta de una mujer en una postura de yoga junto al mar al atardecer.' }],
  ['fruits', { pt_BR: 'Morangos, amoras e laranja-sanguínea vistos de cima sobre fundo branco.', en_US: 'Strawberries, blackberries and blood orange seen from above on white.', es_ES: 'Fresas, moras y naranja sanguina vistas desde arriba sobre fondo blanco.' }],
  ['row', null],
];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const elapsed = iso => Date.now() - new Date(iso).getTime();

// The mock's image model (2.9.0): the community or gallery photo whose description shares the
// most words with the subject, else a gradient card with the subject written on it.
const galleryCandidates = () => GALLERY.filter(([, description]) => description).map(([key, description]) => ({ text: Object.values(description).join(' '), value: LIBRARY[key] }));
function mockGenerate({ subject, box }) {
  const [best] = rankByWords(subject, [...communityCandidates(), ...galleryCandidates()]);
  return best ? { url: best.value.url, width: best.value.width, height: best.value.height } : gradientCard(subject, box);
}
function gradientCard(subject, box) {
  const ratio = box.width / box.height || 1;
  const width = ratio >= 1 ? 1200 : Math.round(1200 * ratio);
  const height = ratio >= 1 ? Math.round(1200 / ratio) : 1200;
  try {
    const canvas = window.document.createElement('canvas');
    Object.assign(canvas, { width, height });
    const context = canvas.getContext('2d');
    if (!context) return null;
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#2f6fde');
    gradient.addColorStop(1, '#c6f36b');
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    const size = Math.round(Math.min(width, height) / 14);
    Object.assign(context, { font: `600 ${size}px Inter, sans-serif`, fillStyle: '#ffffff', textAlign: 'center', textBaseline: 'middle' });
    const lines = subject.split(/\s+/).reduce((list, word) => {
      const last = list[list.length - 1];
      if (last && context.measureText(`${last} ${word}`).width <= width * 0.8) list[list.length - 1] = `${last} ${word}`; else list.push(word);
      return list;
    }, []).slice(0, 6);
    lines.forEach((line, index) => context.fillText(line, width / 2, height / 2 + (index - (lines.length - 1) / 2) * size * 1.3));
    return { url: canvas.toDataURL('image/jpeg', 0.86), width, height, mime_type: 'image/jpeg' };
  } catch { return null; }
}

export function mockScenario(location = window.location, storage = window.sessionStorage) {
  const params = new URLSearchParams(location.search);
  const flag = params.get('mock');
  const requested = MOCK_SCENARIOS[params.get('scenario')] ? params.get('scenario') : DEFAULT_SCENARIO;
  try {
    if (flag === 'false') { storage.removeItem(MOCK_KEY); return null; }
    if (flag === 'true') { storage.setItem(MOCK_KEY, requested); return requested; }
    const stored = storage.getItem(MOCK_KEY);
    return MOCK_SCENARIOS[stored] ? stored : null;
  } catch { return flag === 'true' ? requested : null; }
}

export function createMockStudioApi(scenario, storage = window.sessionStorage) {
  const fake = createFakeStudioApi();
  const { server } = fake;
  const config = MOCK_SCENARIOS[scenario] || MOCK_SCENARIOS[DEFAULT_SCENARIO];
  const key = `${STATE_KEY}:${scenario}`;
  const seed = () => {
    server.reset();
    const db = server.db();
    db.features.social_onboarding = config.onboarding;
    db.features.audio = true;
    db.lookup = config.lookup || 'found';
    db.analysis = config.analysis || 'findings';
    if (config.platforms) db.onboardingPlatforms = config.platforms;
    // 2.0.0: the account and its first profile; `profiles` connects two more, all alike.
    if (config.creator) server.seedCreator({ locale: 'pt_BR', handle: 'nutri.carol', name: 'Carol Nutri', interests: ['Hipertrofia', 'Receitas proteicas'],
      extraProfiles: config.profiles ? sampleProfiles() : [] });
    // Subscribed a month ago and twelve days into the second cycle (so an upgrade preview
    // carries a realistic credit), with a pack bought in between: invoices to list.
    if (config.plan) {
      const day = 864e5;
      const started = Date.now() - (config.status === 'past_due' ? 2 : 12) * day;
      server.seedSubscription(config.plan, { started, since: started - 30 * day, status: config.status || 'active', cancel_at_period_end: !!config.cancel });
      server.seedPack(20, started - 9 * day);
    }
    if (config.credits !== undefined) { db.credits = config.credits; db.payments.purchased = config.credits; }
    db.performance = config.performance || 'ready';
    if (config.personality) { db.features.personality = true; server.seedPersonality(); }
    if (config.directMessages) server.seedDirectMessages(db.creator.profiles[0].id, config.directMessages, { contacts: config.directMessages === 'empty' ? 0 : 128 });
  };
  try {
    const saved = JSON.parse(storage.getItem(key));
    if (saved?.db) server.restore(saved); else seed();
  } catch { seed(); }
  // Trending topics with their sources, also in a store saved before they existed. Plan
  // scenarios let the plan decide (Pro only, as in core); the others keep them on for the demo.
  server.db().features.trending_topics = !config.plan;
  server.db().features.payments = true;
  // Contract 1.9.0: more Instagram profiles through the @ onboarding, on in every scenario.
  server.db().features.multiple_profiles = true;
  // Contract 2.3.0: Personality, on in every scenario; 2.5.0 its comparison by reach too.
  server.db().features.personality = true;
  server.db().performance = config.performance || 'ready';
  if (!server.db().topics.some(topic => topic.niche_tags?.length)) server.db().topics = sampleTopics();
  // Contract 2.7.0 proposal: single-post and carousel arts, on in every scenario.
  server.db().artFormats = ['instagram::post', 'instagram::carousel'];
  // Contract 2.8.0 proposal: personal templates (imported or saved from an art), on in every scenario.
  delete server.db().features.art_template_import;
  server.db().features.art_personal_templates = true;
  // Contract 2.9.0: the image question with AI generation, and the community gallery, on in every scenario.
  server.db().features.art_image_generation = true;
  server.db().features.community_images = true;
  const assetsKey = `${ASSETS_KEY}:${scenario}`;
  try { Object.assign(server.db().assetUrls, JSON.parse(storage.getItem(assetsKey)) || {}); } catch { /* No uploads kept yet. */ }
  const persist = () => {
    const state = server.state();
    const uploads = Object.fromEntries(Object.entries(state.db.assetUrls || {}).filter(([, url]) => url.startsWith('data:')));
    // A video's object URL dies with the page: it is never kept.
    state.db.assetUrls = Object.fromEntries(Object.entries(state.db.assetUrls || {}).filter(([, url]) => !url.startsWith('data:') && !url.startsWith('blob:')));
    try { storage.setItem(key, JSON.stringify(state)); } catch { /* The mock still works for this page. */ }
    try { storage.setItem(assetsKey, JSON.stringify(uploads)); } catch { /* Too large to keep across reloads. */ }
  };

  // The art worker: designs a first art from the brief (template, brand, format: a single post
  // or a carousel, whose page count comes from the prompt) or applies a chat edit to the base
  // revision, scoped to the layers the run targets. The images sent with the run's message
  // are then placed as the creator asked (placeAttachments); the places still wanting an image
  // are asked about afterwards (finishArt).
  const designArt = run => {
    const db = server.db();
    const p = db.projects.find(item => item.id === run.project_id);
    const profile = db.creator?.profiles?.find(item => item.id === p.profile_id);
    const locale = profile?.locale || db.creator?.locale || 'pt_BR';
    const format = p.brief.format;
    const inputs = db.artRunInputs[run.id] || { target_layer_ids: [], template_id: null };
    const source = db.messages.find(item => item.id === run.source_message_id);
    const base = run.base_revision_id ? server.revisionDocument(run.base_revision_id) : null;
    const brandKit = server.brandKit(p.profile_id);
    const templateId = inputs.template_id || (run.kind === 'generate'
      ? pickTemplate(source?.text, { templateId: p.brief.art?.template_id, pastDesigns: server.pastDesigns(p.profile_id), format }) : null);
    const personalDoc = templateId && !templateEntry(templateId) ? server.personalDocument(templateId) : null;
    const templateDoc = templateId ? (templateEntry(templateId) ? null : personalDoc || server.pastDocument(templateId)) : null;
    const template = templateId ? { id: templateId, source: templateEntry(templateId) ? templateEntry(templateId).source : personalDoc ? 'personal' : 'past_design', document: templateDoc } : null;
    const attached = (source?.attachments || []).map(item => ({ role: item.role, source: server.assetSource(item.asset_id) })).filter(item => item.source);
    let payload;
    // A chat edit that brings images, or asks for some, is about them: they are placed (or asked about), the rest stays.
    if (run.kind === 'revise' && base && (attached.length || asksForImages(source?.text)) && !inputs.template_id) payload = { document: base, change_summary: '', changed_layer_ids: [] };
    else if (run.kind === 'revise' && base && !inputs.template_id) payload = reviseArt({ document: base, instruction: source?.text, targetLayerIds: inputs.target_layer_ids, locale });
    else if (run.kind === 'revise' && base) {
      const next = templateDoc || templateDocument(templateId, { locale, handle: profile?.handle, brandKit, format });
      payload = { document: applyTemplate(base, next), change_summary: generateArt({ prompt: '', locale }).change_summary, changed_layer_ids: [] };
    } else {
      payload = generateArt({ prompt: source?.text, locale, handle: profile?.handle, brandKit, aspectRatio: p.brief.art?.aspect_ratio, template, format });
    }
    if (attached.length) {
      const placed = placeAttachments({ document: payload.document, attachments: attached, prompt: source?.text, locale });
      const summary = [payload.change_summary.replace(/\.$/, ''), ...placed.notes].filter(Boolean).join('. ');
      payload = { ...payload, document: placed.document, change_summary: `${summary}.`, changed_layer_ids: [...new Set([...payload.changed_layer_ids, ...placed.changed])] };
    }
    return { ...payload, template_id: templateId || (base ? undefined : null), suggestions: artSuggestions(locale), warnings: payload.warnings || [] };
  };
  // Text is fitted with the faces it will paint in: they load first, then the art is laid
  // out again with their measurements (fonts are client-side, as the real worker's are not).
  const designed = async run => {
    let payload = designArt(run);
    const fonts = await ensureDocumentFonts(payload.document);
    if (fonts.ok) { invalidateTextLayouts(); payload = designArt(run); }
    return payload;
  };
  const working = run => ['queued', 'running'].includes(server.db().runs.find(item => item.id === run.id)?.status);
  const finishing = new Set();
  const work = (run, task) => {
    if (finishing.has(run.id)) return;
    finishing.add(run.id);
    (async () => {
      try { await task(); persist(); } finally { finishing.delete(run.id); }
    })();
  };
  // Designs waiting for review and persisting (kept in this page only: a reload designs again).
  const designs = new Map();
  // When `designing` ends: the run asks about the places that want an image (2.9.0), or its
  // design waits to be saved.
  const designRun = run => work(run, async () => {
    const payload = await designed(run);
    if (working(run) && !server.askImages(run.id, payload)) designs.set(run.id, payload);
  });
  // The art is saved: with the answer's images placed (generated by `mockGenerate`), or as designed.
  const finishArt = run => work(run, async () => {
    if (server.imageAnswer(run.id)) {
      if (working(run)) server.succeedArt(run.id, server.placeImages(run.id, { generate: mockGenerate }));
      return;
    }
    const payload = designs.get(run.id) || await designed(run);
    if (!designs.delete(run.id) && (!working(run) || server.askImages(run.id, payload))) return;
    if (working(run)) server.succeedArt(run.id, payload);
  });
  const seedGalleries = () => {
    const db = server.db();
    db.mockGalleries = db.mockGalleries || {};
    (db.creator?.profiles || []).forEach(profile => {
      if (db.mockGalleries[profile.id]) return;
      db.mockGalleries[profile.id] = true;
      GALLERY.forEach(([key, description]) => server.seedArtImage(profile.id, { ...LIBRARY[key], description: description ? description[profile.locale] || description.pt_BR : null }));
    });
  };

  // The worker's part, settled on every request by elapsed time.
  const advance = () => {
    const db = server.db();
    if (db.onboarding?.status === 'analyzing' && elapsed(db.onboarding.timestamps.created) >= ANALYSIS_MS) server.analyze();
    db.transcriptions.filter(item => item.status === 'queued' && elapsed(item.timestamps.created) >= TRANSCRIPTION_MS)
      .forEach(item => server.transcribe(item.id, sampleTranscript(item.locale)));
    Object.entries(db.payments?.paid || {}).filter(([, at]) => elapsed(at) >= WEBHOOK_MS).forEach(([id]) => {
      server.fulfillCheckout(id);
      delete db.payments.paid[id];
    });
    (db.personalityMappings || []).filter(mapping => ['queued', 'running'].includes(mapping.status)).forEach(mapping => {
      const age = elapsed(mapping.timestamps.created);
      if (age >= MAPPING_DONE_MS) {
        // The scenario's quiet video: the second one of the first mapping, for good.
        const quiet = db.mockQuiet || (db.mockQuiet = []);
        if (config.personalityFailure && !quiet.length) quiet.push(mapping.sources[1].video_id);
        const failures = Object.fromEntries(mapping.sources.map((source, index) => [index, quiet.includes(source.video_id) ? config.personalityFailure : null]).filter(([, code]) => code));
        if (Object.keys(failures).length) server.failPersonality(mapping.id, failures); else server.completePersonality(mapping.id);
        return;
      }
      const step = MAPPING_STEPS.filter(([at]) => age >= at).pop();
      // Without a sample to compare, the comparing slot is spent analyzing.
      const stage = step?.[1] === 'comparing' && !db.performanceSamples?.[mapping.id]?.videos.length ? 'analyzing' : step?.[1];
      const ready = mapping.sources.filter(source => source.status === 'ready').length;
      if (step && (mapping.stage !== stage || (stage === 'transcribing' && ready < step[2]))) server.advancePersonality(mapping.id, stage, step[2]);
    });
    seedGalleries();
    db.runs.filter(run => ['queued', 'running'].includes(run.status)).forEach(run => {
      const age = elapsed(run.timestamps.created);
      const source = db.messages.find(item => item.id === run.source_message_id);
      if (source?.text.trim().endsWith('?')) {
        if (age >= ANSWER_MS) server.answer(run.id, sampleAnswer(db.creator?.locale));
        else if (run.status === 'queued' && age >= RUN_STEPS[0][0]) { run.status = 'running'; run.version += 1; run.timestamps.updated = new Date().toISOString(); }
        return;
      }
      const artRun = server.isArt(db.projects.find(item => item.id === run.project_id));
      // An answered image question: its images phase, then review and persisting, timed from the answer.
      const answered = artRun ? server.imageAnswer(run.id) : null;
      if (answered) {
        const since = elapsed(answered.at) - IMAGES_MS - answered.generated * IMAGE_GENERATE_MS;
        if (since >= IMAGES_DONE_MS) { finishArt(run); return; }
        const phase = since < 0 ? 'images' : IMAGES_TAIL.filter(([at]) => since >= at).pop()[1];
        if (run.phase !== phase) { run.phase = phase; run.version += 1; run.timestamps.updated = new Date().toISOString(); }
        return;
      }
      if (artRun && age >= ART_DONE_MS) { finishArt(run); return; }
      // Designed, the art stays on `designing` until `designRun` has decided: a question parks
      // the run (review and persisting play after the answer), else its design moves on.
      const deciding = artRun && age >= ART_DESIGNED_MS && !designs.has(run.id);
      if (deciding) designRun(run);
      if (!artRun && age >= RUN_DONE_MS) { server.succeed(run.id, sampleScript(db.creator?.locale), undefined, sampleSuggestions(db.creator?.locale)); return; }
      const step = deciding ? [ART_DESIGNED_MS, 'running', 'designing'] : (artRun ? ART_STEPS : RUN_STEPS).filter(([at]) => age >= at).pop();
      if (step && (run.status !== step[1] || run.phase !== step[2])) {
        run.status = step[1]; run.phase = step[2]; run.version += 1; run.timestamps.updated = new Date().toISOString();
      }
    });
  };

  const api = {};
  Object.entries(fake).forEach(([name, route]) => {
    if (name === 'server') return;
    api[name] = async (...args) => {
      await wait(LATENCY_MS[name] ?? 250 + Math.random() * 250);
      advance();
      try { return await route(...args); } finally { persist(); }
    };
  });
  // The presigned PUT: an art image's bytes are kept (downscaled, as a data URL) so the
  // gallery and the arts can show it; other uploads are only acknowledged.
  // A video (2.13.0) is kept as an object URL for this page only (too large for storage), and
  // its upload walks through `onProgress`.
  api.putUpload = async (upload, blob, mimeType, { onProgress } = {}) => {
    const id = upload.url.split('/').pop();
    const purpose = server.db().assets.find(item => item.id === id)?.purpose;
    if (purpose === 'art_video' && blob) {
      for (let step = 1; step <= 10; step++) { await wait(120); onProgress?.(step / 10); }
      server.db().assetUrls[id] = URL.createObjectURL(blob);
      return null;
    }
    await wait(300);
    if (purpose === 'art_image' && blob) {
      const small = await prepareImageFile(blob, { maxSide: 1400, quality: 0.84 });
      server.db().assetUrls[id] = await blobToDataUrl(small.blob);
      persist();
    }
    return null;
  };
  return {
    api,
    scenario,
    scenarios: Object.keys(MOCK_SCENARIOS),
    // The embedded checkout's part: the session is paid; its webhook comes later.
    pay: async checkoutId => { await wait(900); try { return server.payCheckout(checkoutId); } finally { persist(); } },
    // The Meta dialog's part (2.14.0): how the authorization ends, as the OAuth callback redirects.
    completeConnection: async (connectionId, outcome) => { await wait(700); try { return server.completeConnection(connectionId, outcome); } finally { persist(); } },
    // Someone comments the keyword: an execution, its messages in the Direct and the counters.
    simulateTrigger: async automationId => { await wait(400); try { return server.simulateTrigger(automationId); } finally { persist(); } },
    // Clears this scenario's store; the next load seeds it again.
    reset: () => { try { storage.removeItem(key); } catch { /* Nothing stored. */ } },
  };
}
